import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { AsyncDialog, ConfirmationDialog } from '../../components/AsyncDialog';
import { AsyncDialogRef } from '../../components/AsyncDialog/AsyncDialog';
import { ConfirmationDialogData } from '../../components/AsyncDialog/ConfirmationDialog';
import { SectionCard } from '../../components/Section';
import COLORS from '../../constants/colors';
import { usePermissions } from '../../hooks';
import { useAppDispatch } from '../../redux/hooks';
import { triggerJadwaTransfer } from '../../redux/modules/jadwa/jadwa.actions';
import { jadwaSelector } from '../../redux/modules/jadwa/jadwa.selectors';
import { TriggerJadwaTransferRequest } from '../../types/jadwa';
import * as permissions from '../../utils/permissions';

type TransferType = TriggerJadwaTransferRequest['type'];
type FundManager = TriggerJadwaTransferRequest['fundManager'];

const fundManagers: { value: FundManager; label: string }[] = [
  { value: 'JADWA', label: 'Jadwa' },
  { value: 'ALBILAD', label: 'Albilad' },
  { value: 'SNB', label: 'SNB Capital' },
];

const transferTypeLabels: Record<TransferType, string> = {
  SUBSCRIPTION: 'Subscription',
  REDEMPTION: 'Redemption',
};

const transferTypeDescriptions: Record<TransferType, string> = {
  SUBSCRIPTION: 'executes every Created mutual-fund subscription order in portfolio and sends the wire from ANB.',
  REDEMPTION: 'executes every Created mutual-fund redemption order in portfolio and creates the transfer to receive.',
};

export function ActionsTab() {
  const { t } = useTranslation('apiError');
  const dispatch = useAppDispatch();
  const asyncDialogRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);
  const { error } = useSelector(jadwaSelector);
  const { enqueueSnackbar } = useSnackbar();
  const canTriggerTransfer = usePermissions({ items: [permissions.JadwaPermissions.triggerTransfer] });
  // Which button is running — the store's status is shared by all six, so it would spin every one of them.
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const onTrigger = async (fundManager: FundManager, label: string, type: TransferType) => {
    const action = `Trigger ${label} ${transferTypeLabels[type]}`;
    const isConfirmed = await asyncDialogRef.current?.show({
      title: action,
      description: `This runs now: it ${transferTypeDescriptions[type]} Only orders of ${label} are picked up. It can't be undone from here.`,
      acceptButtonText: 'Trigger',
    });

    if (!isConfirmed) {
      return;
    }

    setPendingKey(`${fundManager}-${type}`);

    try {
      await dispatch(triggerJadwaTransfer({ type, fundManager })).unwrap();

      enqueueSnackbar(`${action} triggered successfully!`, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(`${action} failed: ${t(err?.message)} ${JSON.stringify(err?.data)}`, {
        variant: 'error',
      });
    } finally {
      setPendingKey(null);
    }
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      <SectionCard
        title="Fund manager transfers"
        description="Runs the fund manager job now instead of waiting for its cron. Each button picks up only that manager's Created mutual-fund orders of that type."
        disablePadding
      >
        <Stack divider={<Divider />}>
          {fundManagers.map(({ value, label }) => (
            <Box
              key={value}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px',
                px: '20px',
                py: '14px',
              }}
            >
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: COLORS.MAIN_DARK }}>{label}</Typography>

              <Stack direction="row" spacing={1}>
                <LoadingButton
                  type="button"
                  size="small"
                  color="primary"
                  variant="contained"
                  startIcon={<CallMadeIcon />}
                  loading={pendingKey === `${value}-SUBSCRIPTION`}
                  loadingPosition="start"
                  disabled={!canTriggerTransfer || Boolean(pendingKey)}
                  onClick={() => onTrigger(value, label, 'SUBSCRIPTION')}
                >
                  {transferTypeLabels.SUBSCRIPTION}
                </LoadingButton>
                <LoadingButton
                  type="button"
                  size="small"
                  color="primary"
                  variant="outlined"
                  startIcon={<CallReceivedIcon />}
                  loading={pendingKey === `${value}-REDEMPTION`}
                  loadingPosition="start"
                  disabled={!canTriggerTransfer || Boolean(pendingKey)}
                  onClick={() => onTrigger(value, label, 'REDEMPTION')}
                >
                  {transferTypeLabels.REDEMPTION}
                </LoadingButton>
              </Stack>
            </Box>
          ))}
        </Stack>
      </SectionCard>

      <AsyncDialog ref={asyncDialogRef} dialog={ConfirmationDialog} />
    </>
  );
}
